import { FC, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { DotLottieReact, type DotLottie } from '@lottiefiles/dotlottie-react';
import { ArrowRight } from 'lucide-react';

interface AutoSubmitOverlayProps {
  /** Whether the overlay is shown. Kept mounted for enter/exit animation via AnimatePresence. */
  visible: boolean;
  /** True once the diagnostic has actually submitted — required before the results button appears. */
  submitReady: boolean;
  /** "Show me my results" — proceed to the finished / roadmap-building flow. */
  onShowResults: () => void;
}

// The celebration Lottie runs ~10s; consider the scene "settled" (button eligible) after ~6.5s.
const ANIMATION_SETTLE_MS = 6500;

/**
 * Full-screen celebration shown when the diagnostic auto-submits early (TEAMB-3395). The whole scene
 * — confetti, progress bar, headline/subcopy and the mascot — lives in the self-hosted Lottie
 * (frontend/public/dg-auto-submit-animation.json); the only HTML on top is the "Show me my results"
 * button. It is identical for every early finish so it never signals *why* the diagnostic ended.
 *
 * The button appears only once the animation has settled AND the submit has succeeded (`submitReady`),
 * so a failed submit can't advance the student to results with an unsubmitted diagnostic.
 */
const AutoSubmitOverlay: FC<AutoSubmitOverlayProps> = ({ visible, submitReady, onShowResults }) => {
  const [animationSettled, setAnimationSettled] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const showButton = animationSettled && submitReady;

  // Mark the animation settled after its run (backed up by the Lottie 'complete' event).
  useEffect(() => {
    if (!visible) {
      setAnimationSettled(false);
      return undefined;
    }
    const timer = setTimeout(() => setAnimationSettled(true), ANIMATION_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [visible]);

  // Hide the app footer while the celebration is up (header stays visible) — CSS in index.scss.
  useEffect(() => {
    if (!visible) return undefined;
    document.body.classList.add('dg-auto-submit-open');
    return () => document.body.classList.remove('dg-auto-submit-open');
  }, [visible]);

  // Move focus into the overlay when it opens so keyboard / screen-reader users aren't left on the
  // now-hidden assessment behind it.
  useEffect(() => {
    if (!visible) return undefined;
    const id = setTimeout(() => containerRef.current?.focus(), 0);
    return () => clearTimeout(id);
  }, [visible]);

  // Move focus to the results button as soon as it appears.
  useEffect(() => {
    if (showButton) buttonRef.current?.focus();
  }, [showButton]);

  const handleLottieRef = (dotLottie: DotLottie | null) => {
    dotLottie?.addEventListener('complete', () => setAnimationSettled(true));
  };

  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          ref={containerRef}
          role="dialog"
          aria-label="Finishing your diagnostic"
          data-testid="auto-submit-overlay"
          tabIndex={-1}
          className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-neutral-50 tw-bg-focus-grid tw-text-indigo-100 focus:tw-outline-none"
          style={{ zIndex: 10000 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.32, ease: [0.2, 0.8, 0.2, 1] }}
        >
          {/* Aspect-locked box matching the animation canvas (1200x900), sized to fit the viewport, so
              the overlaid button stays aligned with the animation at any window size. */}
          <div
            className="tw-relative"
            style={{ aspectRatio: '1200 / 900', width: 'min(100vw, calc(100vh * 1200 / 900))' }}
          >
            <DotLottieReact
              src="/dg-auto-submit-animation.json"
              autoplay
              loop={false}
              dotLottieRefCallback={handleLottieRef}
              className="tw-h-full tw-w-full"
            />

            {/* Button reveals once the animation has settled and the submit has succeeded. */}
            <div className="tw-absolute tw-left-1/2 tw-top-[80%] tw--translate-x-1/2">
              <AnimatePresence>
                {showButton && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.28, ease: [0.2, 0.8, 0.2, 1] }}
                  >
                    <button
                      ref={buttonRef}
                      type="button"
                      data-testid="auto-submit-show-results"
                      onClick={onShowResults}
                      className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-full tw-bg-indigo-600 tw-px-7 tw-py-[14px] tw-font-atkinson tw-text-lg tw-font-bold tw-text-neutral-0 tw-cursor-pointer tw-border-none hover:tw-bg-indigo-700 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-indigo-600"
                    >
                      Show me my results
                      <ArrowRight size={20} />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default AutoSubmitOverlay;
